import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { cn } from "@/lib/cn";
import { formatCurrency, formatNumber, formatPercentRaw, safeRatio } from "@/lib/format";
import type { MetaCampaign } from "@/lib/types";
import { usePayload } from "@/lib/usePayload";
import { getMetaCampaigns } from "@/lib/viewModeSelectors";

export function MetaCampaigns() {
  const { payload, isLoading, viewMode, monthLabel } = usePayload();
  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;
  
  const allCampaigns = getMetaCampaigns(payload, viewMode);
  
  // Separate Builder campaigns from Standard Meta campaigns
  const isBuilder = (c: MetaCampaign) => (c.campaign_name || "").toLowerCase().includes("builder");
  const standardCampaigns = allCampaigns.filter((c: MetaCampaign) => !isBuilder(c));
  const builderCampaigns = allCampaigns.filter((c: MetaCampaign) => isBuilder(c));

  // Top KPI summary excludes Builder spend to reflect core Meta campaigns
  const standardActiveCount = standardCampaigns.filter((c: MetaCampaign) => (c.effective_status || "").toUpperCase() === "ACTIVE").length;
  const standardSpend = standardCampaigns.reduce((s: number, c: MetaCampaign) => s + (c.spend || 0), 0);
  const standardResults = standardCampaigns.reduce((s: number, c: MetaCampaign) => s + (c.results || 0), 0);
  const standardImpressions = standardCampaigns.reduce((s: number, c: MetaCampaign) => s + (c.impressions || 0), 0);
  const standardClicks = standardCampaigns.reduce((s: number, c: MetaCampaign) => s + (c.clicks || 0), 0);
  const avgCtr = standardImpressions ? (standardClicks / standardImpressions) * 100 : null;

  const builderActiveCount = builderCampaigns.filter((c: MetaCampaign) => (c.effective_status || "").toUpperCase() === "ACTIVE").length;
  const builderSpend = builderCampaigns.reduce((s: number, c: MetaCampaign) => s + (c.spend || 0), 0);

  const displayLabel = viewMode === "current_week" ? (payload.weekly_reporting?.week_label || "Current Week") : monthLabel;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Meta Campaigns</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Facebook & Instagram campaign performance — {displayLabel}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard accent="green" label="Active Meta Campaigns" value={formatNumber(standardActiveCount)} />
        <KpiCard accent="blue" label="Core Meta Spend" value={formatCurrency(standardSpend, { compact: true })} />
        <KpiCard accent="cyan" label="Total Results" value={formatNumber(standardResults)} />
        <KpiCard
          accent="amber"
          label="Avg CTR"
          value={avgCtr != null ? formatPercentRaw(avgCtr / 100) : "—"}
        />
      </div>

      {/* Main Standard Meta Campaigns Panel */}
      <Panel
        title="Meta Campaign Details (Excludes Builder)"
        right={
          <span className="pill bg-green-50 border border-green-200 text-green-700">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            <span className="numeric-value font-semibold">{formatNumber(standardActiveCount)}</span> Active
          </span>
        }
      >
        {standardCampaigns.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No standard Meta campaigns reported.</div>
        ) : (
          <CampaignTable campaigns={standardCampaigns} />
        )}
      </Panel>

      {/* Dedicated Builder Campaigns Panel */}
      <Panel
        title="Builder Campaigns"
        right={
          <div className="flex items-center gap-2">
            <span className="pill bg-blue-50 border border-blue-200 text-blue-700 text-xs px-2.5 py-1">
              Spend: <span className="font-semibold">{formatCurrency(builderSpend)}</span>
            </span>
            <span className="pill bg-slate-50 border border-slate-200 text-slate-700 text-xs px-2.5 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block mr-1" />
              {formatNumber(builderActiveCount)} Active
            </span>
          </div>
        }
      >
        {builderCampaigns.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No Builder campaigns reported for this period.</div>
        ) : (
          <CampaignTable campaigns={builderCampaigns} />
        )}
      </Panel>
    </div>
  );
}

function CampaignTable({ campaigns }: { campaigns: MetaCampaign[] }) {
  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left label-xs border-b border-border">
            <th className="font-medium px-5 py-2.5">Campaign</th>
            <th className="font-medium px-3 py-2.5">Objective</th>
            <th className="font-medium px-3 py-2.5">Status</th>
            <th className="font-medium px-3 py-2.5 text-right">Spend</th>
            <th className="font-medium px-3 py-2.5 text-right">Results</th>
            <th className="font-medium px-3 py-2.5 text-right">CPR</th>
            <th className="font-medium px-3 py-2.5 text-right">Impressions</th>
            <th className="font-medium px-3 py-2.5 text-right">Clicks</th>
            <th className="font-medium px-5 py-2.5 text-right">CTR</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const isActive = (c.effective_status || "").toUpperCase() === "ACTIVE";
            const ctr = safeRatio(c.clicks, c.impressions);
            return (
              <tr key={c.campaign_name} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                <td className="px-5 py-2.5 font-medium">{c.campaign_name}</td>
                <td className="px-3 py-2.5">
                  <span className="pill bg-slate-100 text-slate-700 border border-slate-200 text-[10px] tracking-wider">
                    {c.objective || "—"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-green-500" : "bg-slate-400")} />
                    <span className={isActive ? "text-green-700" : "text-slate-500"}>
                      {isActive ? "Active" : "Paused"}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatCurrency(c.spend, { compact: true })}</td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatNumber(c.results)}</td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.cost_per_result ? formatCurrency(c.cost_per_result, { compact: true }) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatNumber(c.impressions)}</td>
                <td className="px-3 py-2.5 text-right numeric-value">{formatNumber(c.clicks)}</td>
                <td className="px-5 py-2.5 text-right numeric-value">
                  {ctr != null ? formatPercentRaw(ctr) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
